// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "../../theme/SwiperOverrides.css";

import * as styles from "./ImageCarouselModal.module.scss";

import React, { useEffect, useState } from "react";
// import Swiper core and required modules
import { A11y, Navigation, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import ReactModal from "react-modal";
import { useIsTouchDevice } from "../../hooks/useIsTouchDevice";
import { Colours } from "../../utils/colours";
import { Pagination } from "../Pagination/Pagination";
import SwiperImage from "../SwiperImage/SwiperImage";
import { Cross, RotateDevice } from "../common/Icons";

export interface ImageProps {
	uri?: string;
	src: string;
	alt: string;
}
export interface ImageCarouselModalProps {
	expanded: boolean;
	onHide: () => void;
	images: ImageProps[];
	isDog?: boolean;
	activeSlide?: number;
}

export const ImageCarouselModal: React.FC<ImageCarouselModalProps> = ({
	expanded,
	onHide,
	images,
	isDog = false,
	activeSlide,
}) => {
	const [currentSlide, setCurrentSlide] = useState(activeSlide);
	const [swiper, setSwiper] = useState(null);
	const touchDevice = useIsTouchDevice();

	useEffect(() => {
		if (expanded) {
			document.getElementsByTagName("body")[0].style.overflow = "hidden";
		} else {
			document.getElementsByTagName("body")[0].style.overflow = "scroll";
		}
		return () => {
			document.getElementsByTagName("body")[0].style.overflow = "scroll";
		};
	}, [expanded]);

	return (
		<ReactModal
			isOpen={expanded}
			onRequestClose={onHide}
			style={{
				overlay: {
					position: "fixed",
					display: "flex",
					height: "100%",
					width: "100%",
					insetBlockStart: 0,
					insetInlineStart: 0,
					padding: 0,
					margin: 0,
					zIndex: 10001,
					backgroundColor: "#222222",
					transform: "none",
					transition: "opacity 0.3s ease-in-out",
				},
				content: {
					position: "relative",
					display: "flex",
					insetBlockStart: 0,
					insetInlineStart: 0,
					border: 0,
					margin: 0,
					padding: 0,

					height: "100%",
					width: "100%",
					backgroundColor: "transparent",
					justifyContent: "center",
					transform: "none",
					transition: "opacity 0.8s ease-in-out",
					opacity: 1,
				},
			}}
		>
			<div className={styles.modal}>
				<button
					type="button"
					aria-label="Close error message"
					className={styles.closebutton}
					onClick={onHide}
				>
					<Cross fill={Colours.WHITE} height={"24px"} width={"24px"} />
				</button>
				<div className={styles.rotatedevice}>
					<RotateDevice height={"24px"} width={"24px"} />
					<div className={styles.rotatetext}>
						Rotate your device to view larger photos
					</div>
				</div>
				<div className={styles.modalswiper}>
					<Swiper
						className={styles.modalcontainer}
						modules={[Virtual, Navigation, A11y]}
						spaceBetween={0}
						slidesPerView={1}
						edgeSwipeThreshold={10}
						threshold={0}
						grabCursor={true}
						longSwipes={true}
						resistance={false}
						shortSwipes={!!touchDevice}
						loop={false}
						effect="slide"
						centeredSlides={true}
						scrollbar={{ draggable: true }}
						onSwiper={(swiper) => setSwiper(swiper)}
						onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
						initialSlide={activeSlide}
					>
						{images.map((fieldImage) => (
							<SwiperSlide key={fieldImage.src || fieldImage.uri}>
								<SwiperImage isDog={isDog} {...fieldImage} isStandalone />
							</SwiperSlide>
						))}
					</Swiper>
					<div className={styles.pagination}>
						<Pagination
							onFirstClick={() => swiper.slideTo(0)}
							onLastClick={() => swiper.slideTo(images.length - 1)}
							onNextClick={() => swiper.slideNext()}
							onPreviousClick={() => swiper.slidePrev()}
							firstDisabled={currentSlide === 0}
							previousDisabled={currentSlide === 0}
							nextDisabled={currentSlide === images.length - 1}
							lastDisabled={currentSlide === images.length - 1}
							noOffset={true}
							position={currentSlide}
							slim={true}
							total={images.length}
						/>
					</div>
				</div>
			</div>
		</ReactModal>
	);
};

export default ImageCarouselModal;
